import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Spinner,
  Tabs,
  Text,
  VStack,
} from 'wss3-forge'
import { SignOut20Regular } from '@fluentui/react-icons'
import { useAuth } from '../contexts/AuthContext.js'
import { useWebPush } from '../hooks/useWebPush.js'
import {
  getServerInfo,
  getVoiceHealth,
  type ServerInfoResponse,
  type VoiceHealthResponse,
} from '../lib/api.js'

interface TestResultBanner {
  delivered: number
  dropped: number
}

type TabId = 'account' | 'model' | 'voice' | 'notifications'

function describeVoice(h: VoiceHealthResponse | undefined): string {
  if (!h) return 'inconnu'
  if (h.backend === 'realtime') {
    return `OpenAI Realtime · ${h.voice ?? '?'} (${h.model ?? '?'})`
  }
  const stt = h.stt_model ?? '?'
  const tts = h.tts_voice ?? '?'
  const vad = h.vad ?? '?'
  return `Local · STT ${stt} · VAD ${vad} · TTS ${tts}`
}

function AccountTab() {
  const { user, logout } = useAuth()
  if (!user) return <Spinner size="sm" />
  return (
    <Card padding="lg">
      <HStack gap="md" align="center" fullWidth>
        <Avatar
          {...(user.avatar ? { src: user.avatar } : {})}
          name={user.name}
          size="lg"
        />
        <VStack gap="xs" align="start" style={{ flex: 1, minWidth: 0 }}>
          <Text size="md" weight="semibold">{user.name}</Text>
          <Text size="sm" color="muted">{user.email}</Text>
          <Text size="xs" color="muted">id {user.id.slice(0, 8)}…</Text>
        </VStack>
        <Button
          variant="secondary"
          size="sm"
          icon={<SignOut20Regular />}
          onClick={() => void logout()}
        >
          Déconnexion
        </Button>
      </HStack>
    </Card>
  )
}

function ModelTab() {
  const infoQuery = useQuery<ServerInfoResponse>({
    queryKey: ['server-info'],
    queryFn: getServerInfo,
    staleTime: 60_000,
  })
  return (
    <Card padding="lg">
      <VStack gap="md" align="start" fullWidth>
        {infoQuery.isLoading && <Spinner size="sm" />}
        {infoQuery.data && (
          <HStack gap="xs" align="center">
            <Badge variant="default">{infoQuery.data.llm.provider}</Badge>
            <Text>{infoQuery.data.llm.model}</Text>
          </HStack>
        )}
        <Text size="sm" color="muted">
          Cloud par défaut, fallback automatique vers le LLM local en cas de panne.
          Le mode incognito (depuis la page d’accueil) force le LLM local.
          Configuration via <code>GROQ_MODEL</code> / <code>LLM_MODEL</code> dans <code>.env</code>.
        </Text>
      </VStack>
    </Card>
  )
}

function VoiceTab() {
  const voiceQuery = useQuery<VoiceHealthResponse>({
    queryKey: ['voice-health'],
    queryFn: getVoiceHealth,
    staleTime: 60_000,
    retry: 1,
  })
  return (
    <Card padding="lg">
      <VStack gap="md" align="start" fullWidth>
        {voiceQuery.isLoading && <Spinner size="sm" />}
        {voiceQuery.error && (
          <Text size="sm" color="muted">
            Service voix injoignable.
          </Text>
        )}
        {voiceQuery.data && (
          <HStack gap="xs" align="center">
            <Badge variant="default">{voiceQuery.data.backend}</Badge>
            <Text>{describeVoice(voiceQuery.data)}</Text>
          </HStack>
        )}
        <Text size="sm" color="muted">
          Bascule local vers realtime via <code>VOICE_BACKEND</code> dans
          <code> /opt/stark/.env</code> sur le VPS.
        </Text>
      </VStack>
    </Card>
  )
}

function NotificationsTab() {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTest,
    clearError,
  } = useWebPush()
  const [testResult, setTestResult] = useState<TestResultBanner | null>(null)

  const onSendTest = async (): Promise<void> => {
    setTestResult(null)
    try {
      const res = await sendTest()
      setTestResult(res)
    } catch {
      // useWebPush already wrote the message into `error`.
    }
  }

  return (
    <Card padding="lg">
      <VStack gap="md" align="start" fullWidth>
        <Text color="muted">
          Active les notifications pour recevoir tes rappels et alertes Stark sur cet
          appareil.
        </Text>

        {error && (
          <Banner variant="error" title="Erreur" showCloseButton onClose={clearError}>
            {error}
          </Banner>
        )}

        {!isSupported && (
          <Banner variant="info">
            Ton navigateur ne supporte pas les notifications push. Essaie Chrome, Edge ou
            Safari récent (iOS 16.4+ requiert l’installation de la PWA sur l’écran d’accueil).
          </Banner>
        )}

        {isSupported && permission === 'denied' && (
          <Banner variant="warning">
            Permission refusée. Pour réactiver, va dans les réglages de ton navigateur,
            section Site stark.webba.cloud, puis Notifications.
          </Banner>
        )}

        {isSupported && permission !== 'denied' && !isSubscribed && (
          <Button variant="primary" onClick={() => void subscribe()} loading={isLoading}>
            Activer les notifications
          </Button>
        )}

        {isSupported && isSubscribed && (
          <VStack gap="md" align="start" fullWidth>
            <HStack gap="sm" align="center">
              <Badge variant="success">Activé</Badge>
              <Button variant="ghost" size="sm" onClick={() => void unsubscribe()} loading={isLoading}>
                Désactiver
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void onSendTest()} loading={isLoading}>
                Envoyer un test
              </Button>
            </HStack>

            {testResult && (
              <Banner
                variant={testResult.delivered > 0 ? 'success' : 'warning'}
                showCloseButton
                onClose={() => setTestResult(null)}
              >
                {testResult.delivered > 0
                  ? `Notification envoyée : ${testResult.delivered} délivré${testResult.delivered > 1 ? 's' : ''}`
                  : 'Aucune notification délivrée'}
                {testResult.dropped > 0
                  ? `, ${testResult.dropped} échec${testResult.dropped > 1 ? 's' : ''}`
                  : ''}
                .
              </Banner>
            )}
          </VStack>
        )}
      </VStack>
    </Card>
  )
}

export function SettingsPage() {
  const [tab, setTab] = useState<TabId>('account')
  const infoQuery = useQuery<ServerInfoResponse>({
    queryKey: ['server-info'],
    queryFn: getServerInfo,
    staleTime: 60_000,
  })

  return (
    <Container maxWidth="md" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Réglages</Heading>
          <Text color="muted">Compte, modèle, voix et notifications.</Text>
        </VStack>

        <Tabs
          tabs={[
            { id: 'account', label: 'Compte' },
            { id: 'model', label: 'Modèle' },
            { id: 'voice', label: 'Voix' },
            { id: 'notifications', label: 'Notifications' },
          ]}
          value={tab}
          onChange={(id) => setTab(id as TabId)}
          variant="underline"
        />

        {tab === 'account' && <AccountTab />}
        {tab === 'model' && <ModelTab />}
        {tab === 'voice' && <VoiceTab />}
        {tab === 'notifications' && <NotificationsTab />}

        {infoQuery.data && (
          <Text size="xs" color="muted" align="center">
            Stark · v{infoQuery.data.version} · uptime {Math.round(infoQuery.data.uptime_sec / 60)} min
          </Text>
        )}
      </VStack>
    </Container>
  )
}
